// calibreTierSuffix — resolve a suffixed calibre string ("Ball RR1102-CSL") to
// the BASE calibre plus one of its tiers, using the movement family's canonical
// tier vocabulary as the suffix list.
//
// WHY THIS EXISTS, and why it isn't the tier detection we already had.
// `detectTierFromCalibreString` (calibreTiers.ts) finds a tier name inside a
// string that has ALREADY matched a calibre — it runs in the importer's stamping
// step, after the match. A suffixed string never gets that far: "Ball RR1102-CSL"
// matches no calibre at all, so it lands in the bulk importer's Pending column
// and waits for a human. At 600 references that is not a workable ask.
//
// So this runs one step earlier, at match time, and answers a different
// question: can this string be SPLIT into a base that does match and a suffix
// that names one of that base's tiers?
//
// Three gates, all of which must pass, because a wrong auto-map writes a tier
// link onto every reference carrying the string:
//
//   1. The split point is a real SEPARATOR (space, hyphen, slash, underscore,
//      dot, or a trailing bracketed group). Never a bare character boundary —
//      with a one-character vocabulary entry like "C", a bare boundary would
//      split "SW200C", a calibre whose name simply ends in C.
//   2. The base resolves to a calibre through the caller's own match index, so
//      aliases and brand scoping are honoured exactly as they are everywhere
//      else.
//   3. The suffix names a tier that ACTUALLY EXISTS on that calibre and is
//      visible to the importing brand. The family vocabulary only proposes
//      candidates; the tier on the document is what confirms one. A family
//      listing "CSL" does not mean this member has a CSL tier. And where a
//      family publishes no vocabulary at all, this gate stands alone — see the
//      note on gate 3a below for why silence is not a veto.
//
// Spelling is compared through `matchesTierName`, so the alias table in
// calibreTiers.ts decides what counts as the same grade: "Sellita SW200-1 COSC"
// splits onto a tier named "Chronometer", and "ETA 2892 Chronomètre" onto one
// named "COSC", without either spelling being curated anywhere. Before that,
// this file compared normalized strings for equality while the sibling detector
// (`detectTierFromCalibreString`) consulted the alias table — so the two
// disagreed about the single most common suffix in the catalogue.
//
// Ambiguity is never resolved by guessing: more than one surviving candidate is
// returned in full, for the caller to put to the user.
import { matchesTierName, normalizeForTierMatch, tiersForBrand } from './calibreTiers';
import type { CalibreTier } from '../../services/firebase';

/** Characters a base and its tier suffix may be joined by. */
const SEPARATORS = new Set([' ', '-', '/', '_', '.', '–', '—']);

/** The calibre fields the split needs — a structural subset of CustomCalibreEntry. */
export interface SuffixMatchableCalibre {
  id: string;
  name: string;
  familyId?: string | null;
  data?: { tiers?: CalibreTier[] };
}

export interface TierSuffixCandidate {
  /** `custom_calibres/{id}` of the BASE calibre the string resolved to. */
  calibreId: string;
  /** The base calibre's canonical name, for the row's source line. */
  calibreName: string;
  /** The tier on that calibre the suffix named. */
  tierId: string;
  /** The tier's own name — the canonical spelling, not the input's casing. */
  tierName: string;
  /** The part of the input that matched the base calibre. */
  baseText: string;
  /** The part of the input that matched the tier, as the input spelled it. */
  suffixText: string;
}

export interface DetectTierSuffixInput {
  /** The unresolved calibre string from the import. */
  calibreString: string;
  /**
   * `calibre_families/{id}.tierNames`, keyed by family id — a NARROWING filter,
   * not the source of candidates.
   *
   * A family present with a non-empty list restricts its members' suffixes to
   * that list. A family absent from the map, or present with an empty list,
   * simply doesn't narrow anything: its members fall through to the tiers on
   * their own document. An empty map therefore means "no family narrows
   * anything", NOT "propose nothing" — the reading that used to short-circuit
   * this function and switch the whole feature off for every uncurated family.
   */
  familyTierNames: Map<string, string[]>;
  /**
   * Resolve a base name to a calibre. The caller passes its existing match index
   * (`buildCalibreMatchIndex`), so alias matching and brand visibility are
   * decided in one place rather than reimplemented here.
   */
  resolveBase: (name: string) => SuffixMatchableCalibre | undefined;
  /** The brand being imported into — scopes which tiers may be assigned. */
  brandId?: string;
}

/** Every (base, suffix) pair the string can be split into at a separator. */
function splitPoints(raw: string): Array<{ baseText: string; suffixText: string }> {
  const out: Array<{ baseText: string; suffixText: string }> = [];

  // Trailing bracketed group: "Sellita SW200 (Elaboré)".
  const bracketed = /^(.*\S)\s*[([]([^()[\]]+)[)\]]\s*$/.exec(raw);
  if (bracketed) {
    out.push({ baseText: bracketed[1].trim(), suffixText: bracketed[2].trim() });
  }

  for (let i = 1; i < raw.length - 1; i += 1) {
    if (!SEPARATORS.has(raw[i])) continue;
    const baseText = raw.slice(0, i).trim();
    const suffixText = raw.slice(i + 1).trim();
    if (!baseText || !suffixText) continue;
    out.push({ baseText, suffixText });
  }
  return out;
}

/**
 * Candidate (base calibre + tier) readings of a suffixed calibre string.
 *
 * Empty when the string does not split, when no split's base resolves, or when
 * no resolved base carries a matching tier — every one of which means "leave the
 * row alone and let a human deal with it".
 *
 * Exactly one candidate is safe to apply automatically. More than one means two
 * different bases both read the string, and the caller must ask.
 */
export function detectTierSuffixCandidates({
  calibreString,
  familyTierNames,
  resolveBase,
  brandId,
}: DetectTierSuffixInput): TierSuffixCandidate[] {
  const raw = calibreString.trim();
  if (!raw) return [];

  const out: TierSuffixCandidate[] = [];
  const seen = new Set<string>();

  for (const { baseText, suffixText } of splitPoints(raw)) {
    if (!normalizeForTierMatch(suffixText)) continue;

    const base = resolveBase(baseText);
    if (!base) continue;

    // Gate 3a: WHEN the base's family publishes a vocabulary, the suffix must be
    // in it. A family the calibre doesn't belong to has no say over how its name
    // reads, so this only ever consults the base's OWN family.
    //
    // A base with no `familyId`, or one whose family publishes nothing, skips
    // this gate rather than failing it. `tierNames` is written by exactly one
    // path (the tier editor's "add as new" used to append here; it now writes the structured lists), so tiers
    // created by AI enrich, `backfillCalibreTiers` or `migrateStandaloneToTier`
    // never register a name — and treating that silence as a veto made suffix
    // detection a function of how each calibre's tiers happened to be created,
    // which is invisible from the import screen and read as "it works on some
    // rows". Gate 3b is the gate that carries the safety; 3a was only ever a
    // cheap pre-filter, and a pre-filter with no data should not be a rejection.
    const familyId = base.familyId?.trim();
    const vocab = familyId ? familyTierNames.get(familyId) : undefined;
    if (vocab?.length && !vocab.some((name) => matchesTierName(name, suffixText))) {
      continue;
    }

    // Gate 3b: and the tier must exist on the document, visible to this brand.
    const tier = tiersForBrand(base.data?.tiers, brandId).find((t) =>
      matchesTierName(t.name, suffixText),
    );
    if (!tier) continue;

    const key = `${base.id}::${tier.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      calibreId: base.id,
      calibreName: base.name,
      tierId: tier.id,
      tierName: tier.name,
      baseText,
      suffixText,
    });
  }

  return out;
}
